/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Styles, StyleSheet } from "jss";
import * as css from "csstype";
import {
  Ref,
  ref,
  onUnmounted,
  onBeforeMount,
  provide,
  inject,
  watchEffect
} from "vue";

import { injectJssContext } from "./index";

// Create better type definitions for Jss styles object
type FnValue<R, Data> = R | ((data: Data) => R);
type NormalCssProperties = css.Properties<string | number>;
type CssProperties<Data> = {
  [K in keyof NormalCssProperties]: FnValue<NormalCssProperties[K], Data>;
};

type JssStyleP<S, Data> = CssProperties<Data> & {
  [key: string]: FnValue<JssValue | S, Data>;
};

type JssValue =
  | string
  | number
  | Array<string | number | Array<string | number> | "!important">
  | null
  | false
  | undefined;

// JssStyle creates a work around for a recursive JssStyleP
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface JssStyle<Data>
  extends Partial<JssStyleP<JssStyle<Data>, Data>> {}

export type JStyles<
  Data,
  Name extends string | number | symbol = string
> = Record<
  Name,
  JssStyle<Data> | string | ((data: Data) => JssStyle<Data>) | undefined
>;

// types for prop data

export type PropsData = Record<string, unknown>;
export type PropsDataWithTheme<Local, Theme> = Local &
  (Theme extends undefined | null
    ? Record<string, unknown>
    : { theme?: Theme });

const themeSymbol = Symbol("theme");

/**
 * Provide a theme type parameter to this helper function to generate a
 * set of vue composition api functions for using a themed createUseStyles
 */
export function createThemedStyles<Theme>() {
  /**
   * Use the provided theme in setup function.
   */
  function injectTheme() {
    const result = inject<Readonly<Ref<Theme>>>(themeSymbol);
    if (result) return result;
    return undefined;
  }

  function provideTheme<Theme>(value: Ref<Theme>) {
    provide<Readonly<Ref<Theme>>>(themeSymbol, value);
  }

  /**
   * Creates a vue composition api function that reactively updates styles
   * based on passed ref.
   * @param styles vue jss rule(s)
   * @param initData data that is initially applied to vue jss rules
   */
  function createUseStyles<
    Name extends string | number | symbol,
    T extends PropsData,
    Data = PropsDataWithTheme<T, Theme>
  >(styles: Partial<JStyles<Data, Name>>, initData?: T) {
    return {
      /**
       * Provides classes for rules, that will be dynamically updated when data changes
       * @param reactiveData a ref to data that is watched
       */
      useStyles(reactiveData?: Readonly<Ref<T>>) {
        const { jss } = injectJssContext();

        // for now forcing this type cast override because
        // we know better than the jss types
        const sheet = jss.createStyleSheet(
          (styles as unknown) as Partial<Styles<Name>>,
          { link: true }
        );

        const theme = injectTheme();
        const classes = ref(sheet.classes);

        onBeforeMount(() => {
          sheet.attach();
        });

        watchEffect(() =>
          sheet.update({
            ...initData,
            ...(reactiveData && reactiveData.value),

            // include theme if one is provided
            theme: theme?.value
          })
        );

        onUnmounted(() => {
          jss.removeStyleSheet(sheet as StyleSheet<string>);
        });

        return {
          /**
           * Ref to class names for instance styles
           * @deprecated use classesRef instead
           */
          classes,
          /**
           * Ref to class names for instance styles
           */
          classesRef: classes
        };
      }
    };
  }

  return { provideTheme, injectTheme, createUseStyles };
}
